import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useWorkflow } from "../workflow/WorkflowContext";
import { stepLabelFor } from "../workflow/stepLabels";
import { summarizeStep } from "./CompletedStepsStrip";
import { CheckCircle, Lock, ArrowRight, List, ArrowCounterClockwise } from "@phosphor-icons/react";
import { BlockingMessageDialog } from "./BlockingMessageDialog";

/**
 * v0.4.20 UI8 — 6-step horizontal stepper with hard-disabled locked
 * steps. Mobile: collapses to a single "Step N of 6 · {name}" chip
 * with tap-to-expand drawer (Q-UI8-mobile = Y).
 *
 * Tooltip rules (operator spec 2026-02-15):
 *   - Each disabled step button carries its FIRST blocker string as
 *     `title=` (browsers render this on hover).
 *   - Completed steps are clickable to re-edit; this triggers the
 *     cascade in workflowState.js automatically.
 *   - DOM-collapse of locked content lives in Dashboard.jsx, not here.
 */

// `key` is only used for routing; the visible label is fetched from
// i18n at render time so the stepper translates with the active
// locale.
const STEP_DEFS = [
  { idx: 1, key: "upload" },
  { idx: 2, key: "configure" },
  { idx: 3, key: "calculate" },
  { idx: 4, key: "review" },
  { idx: 5, key: "signoff" },
  { idx: 6, key: "freeze" },
];

export const Stepper = () => {
  const ctx = useWorkflow();
  const { state, activeStep, setActiveStep, resetWorkflow, calc,
          selectedRunIds } = ctx;
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [lockedStepInfo, setLockedStepInfo] = useState(null);

  // v0.6.13 — any step up to the FURTHEST one reached is open, not only
  // completed steps and the one on screen. Before this, stepping back from
  // Row review (incomplete while warnings are open) to Run calculation
  // locked Row review behind its own blockers, so the operator could not
  // return to it except by re-running the calculation.
  const reached = state.activeStep || 1;
  // A1-93 — a stale calc (inputs changed since it ran) is kept and its
  // screens stay viewable up to where it had got, marked "re-run needed";
  // sign-off and lock stay blocked by the step gates themselves.
  const stale = !!state.derived?.calcStale;
  const staleReach = stale ? (state.derived?.staleReach || 0) : 0;
  const canGoto = (idx) => state.steps[idx].complete || idx === activeStep || idx <= reached || idx <= staleReach; // i18n-ignore: code, not copy
  // Reset is only meaningful once the operator has started something.
  // Hides on a fresh page load to avoid visual clutter.
  const hasWorkInProgress = (selectedRunIds?.length || 0) > 0 || !!calc;
  const doReset = () => { resetWorkflow(); setConfirmReset(false); };

  // v0.5.0i+8 — Visual Priority Redesign (Pass 2). Three and only
  // three states, with deliberately exaggerated visual contrast so
  // the operator's eye lands on the active step within 1 second:
  //   🔴 Active  → teal-600 saturated bg, white text, font-bold,
  //                LARGER padding + text, ring + shadow. Dominates.
  //   ✅ Done    → low-contrast muted slate, smaller, no shadow.
  //   ⚪ Locked  → near-invisible slate-300, smaller, no border tint.
  // The active step renders ~30% larger than completed/locked so
  // the hierarchy survives even a black-and-white screenshot.
  const button = (s) => {
    const meta = state.steps[s.idx];
    const isActive = s.idx === activeStep;
    const isComplete = meta.complete;
    const isLocked = !isComplete && !isActive && s.idx > reached && s.idx > staleReach;
    const isStale = stale && s.idx >= 3 && s.idx <= Math.max(staleReach, 3);
    const tooltip = meta.blockers[0] || (isComplete ? t("ui.stepper.click_to_re_edit") : "");
    // v0.6 — completed steps carry their own meta line inline (rows /
    // date range / etc.), consolidating the old CompletedStepsStrip into
    // the stepper so there is ONE status row, not two.
    const metaLine = isStale ? t("ui.stepper.rerun_needed", { defaultValue: "Re-run needed" })
      : (isComplete && !isActive) ? summarizeStep(s.idx, ctx) : null;
    return (
      <button
        key={s.key}
        type="button"
        data-testid={`stepper-step-${s.idx}`}
        data-complete={isComplete}
        data-active={isActive}
        data-locked={isLocked}
        data-stale={isStale}
        aria-disabled={isLocked}
        title={tooltip}
        onClick={() => {
          if (canGoto(s.idx)) { setActiveStep(s.idx); return; }
          // v0.6.8 — this used to be a NATIVELY disabled button, whose
          // only explanation was a hover-only title= (this file's own
          // header comment even documents that as the design) — on the
          // app's primary navigation, invisible on touch devices and
          // easy to miss with a mouse too. Clicking a locked step now
          // shows every blocker (not just the first) as a popup.
          if (meta.blockers.length) {
            setLockedStepInfo({ idx: s.idx, blockers: meta.blockers });
          }
        }}
        className={`relative flex items-center gap-2 border rounded-lg transition-colors ${
          isActive
            ? "px-5 py-3 text-base bg-teal-600 text-white border-teal-700 font-bold shadow-lg shadow-teal-600/30 ring-2 ring-teal-300/50 dark:ring-teal-500/30"
            : !isLocked
              ? "px-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-800/40 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 font-normal hover:bg-slate-100 dark:hover:bg-slate-800"
              : "px-3 py-1.5 text-xs bg-transparent text-slate-400 dark:text-slate-500 border-slate-200/60 dark:border-slate-700/60 font-normal cursor-not-allowed opacity-70"
        }`}
      >
        {isComplete ? <CheckCircle size={14} weight="fill" className="text-slate-400 dark:text-slate-500" />
          : isLocked ? <Lock size={12} weight="bold" />
          : <span className="text-base font-bold w-5 text-center">{s.idx}</span>}
        <span className="flex flex-col items-start leading-tight">
          <span>{stepLabelFor(s.idx, t)}</span>
          {metaLine && (
            <span
              data-testid={`stepper-step-meta-${s.idx}`}
              className="text-[9px] font-normal opacity-70 max-w-[180px] truncate"
            >{metaLine}</span>
          )}
        </span>
        {isActive && (
          <span
            data-testid="stepper-active-caret"
            aria-hidden="true"
            className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-teal-600 dark:text-teal-400"
            style={{ fontSize: "16px", lineHeight: "1" }}
          >▼</span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Desktop */}
      <nav
        data-testid="stepper-desktop"
        className="hidden md:flex items-center gap-1 px-5 py-4 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl"
        aria-label={t("ui.stepper.nav_label", { defaultValue: "Filing workflow steps" })}
      >
        {STEP_DEFS.map((s, i) => {
          const next = STEP_DEFS[i + 1];
          const nextActive = next && next.idx === activeStep;
          const bothDone = state.steps[s.idx].complete
            && next && state.steps[next.idx].complete;
          return (
            <React.Fragment key={s.key}>
              {button(s)}
              {next && (
                <ArrowRight
                  size={nextActive ? 14 : 10}
                  weight="bold"
                  className={`mx-1 ${
                    nextActive
                      ? "text-teal-600 dark:text-teal-400"
                      : bothDone
                        ? "text-slate-300 dark:text-slate-600"
                        : "text-slate-300 dark:text-slate-600"
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
        {hasWorkInProgress && (
          <button
            type="button"
            data-testid="stepper-reset-btn"
            onClick={() => setConfirmReset(true)}
            title={t("ui.stepper.start_over_tooltip")}
            className="ml-auto flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-normal text-slate-600 dark:text-slate-300 hover:text-rose-700 dark:hover:text-rose-300 dark:text-rose-300 border border-slate-200 dark:border-slate-700 hover:border-rose-200 dark:border-rose-800 rounded-xl transition-colors"
          >
            <ArrowCounterClockwise size={12} weight="bold" />
            <span>{t("ui.stepper.start_over")}</span>
          </button>
        )}
      </nav>

      {/* Mobile — single chip + drawer */}
      <nav
        data-testid="stepper-mobile"
        className="md:hidden border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl"
      >
        <button
          type="button"
          data-testid="stepper-mobile-toggle"
          onClick={() => setMobileOpen((s) => !s)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-teal-700 dark:text-teal-300"
      >
        <span className="flex items-center gap-2">
          <List size={14} weight="bold" />
          {t("ui.stepper.mobile_step_of", { active: activeStep, label: stepLabelFor(activeStep, t) })}
        </span>
          <span className="text-slate-500 dark:text-slate-400">{mobileOpen ? "▲" : "▼"}</span>
        </button>
        {mobileOpen && (
          <div className="px-4 pb-3 flex flex-col gap-1">
            {STEP_DEFS.map((s) => button(s))}
            {hasWorkInProgress && (
              <button
                type="button"
                data-testid="stepper-reset-btn-mobile"
                onClick={() => { setMobileOpen(false); setConfirmReset(true); }}
                className="mt-2 flex items-center justify-center gap-1.5 px-3 py-2 text-[10px] tracking-normal text-slate-600 dark:text-slate-300 hover:text-rose-700 dark:hover:text-rose-300 dark:text-rose-300 border border-slate-200 dark:border-slate-700 hover:border-rose-200 dark:border-rose-800 rounded-xl"
              >
                <ArrowCounterClockwise size={12} weight="bold" />
                <span>{t("ui.stepper.start_over")}</span>
              </button>
            )}
          </div>
        )}
      </nav>

      {confirmReset && (
        <div
          data-testid="stepper-reset-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm p-4"
        >
          <div className="bg-white dark:bg-slate-900 border border-rose-200 dark:border-rose-800 max-w-lg w-full p-6 space-y-4 rounded-xl shadow-xl">
            <div className="text-base font-semibold text-rose-700 dark:text-rose-300">
              {t("ui.stepper.reset_modal_title")}
            </div>
            <p
              className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: t("ui.stepper.reset_modal_body") }}
            />
            <div className="flex items-center justify-end gap-3 pt-1">
              <button
                type="button"
                data-testid="stepper-reset-cancel"
                onClick={() => setConfirmReset(false)}
                autoFocus
                className="text-sm font-medium border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/60 dark:bg-slate-950 px-4 py-2 rounded-lg"
              >
                {t("ui.stepper.reset_modal_cancel")}
              </button>
              <button
                type="button"
                data-testid="stepper-reset-confirm"
                data-resolution-class="self-resolve"
                data-resolution-endpoint="navigation"
                onClick={doReset}
                className="bg-rose-600 hover:bg-rose-700 text-white font-semibold text-sm px-4 py-2 rounded-lg"
              >
                {t("ui.stepper.reset_modal_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      <BlockingMessageDialog
        open={!!lockedStepInfo} testid="stepper-locked-step-dialog" severity="warning"
        title={lockedStepInfo
          ? t("ui.stepper.locked_step_title", { step: lockedStepInfo.idx, defaultValue: `Step ${lockedStepInfo.idx} is locked` })
          : ""}
        onOpenChange={(o) => !o && setLockedStepInfo(null)}
      >
        <ul className="list-disc pl-4 space-y-1">
          {(lockedStepInfo?.blockers || []).map((b) => <li key={b}>{b}</li>)}
        </ul>
      </BlockingMessageDialog>
    </>
  );
};
